import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useAuth, API } from "@/App";
import { AdminLayout } from "./Dashboard";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
    Hash, Plus, RefreshCw, Trash2, Twitter, Instagram, Facebook,
    ToggleLeft, ToggleRight, ExternalLink, Loader2, Clock, CheckCircle2, XCircle,
} from "lucide-react";
import { toast } from "sonner";

const PLATFORM_META = {
    x:         { Icon: Twitter,   label: "X",         color: "text-white bg-white/10 border-white/20" },
    instagram: { Icon: Instagram, label: "Instagram", color: "text-pink-300 bg-pink-500/10 border-pink-400/40" },
    facebook:  { Icon: Facebook,  label: "Facebook",  color: "text-blue-300 bg-blue-500/10 border-blue-400/40" },
};

const timeAgo = (iso) => {
    if (!iso) return "never";
    try {
        const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
        if (s < 60) return `${s}s ago`;
        if (s < 3600) return `${Math.floor(s / 60)}m ago`;
        if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
        return `${Math.floor(s / 86400)}d ago`;
    } catch (_) { return ""; }
};


const AddSourceCard = ({ onAdd, busy }) => {
    const [platform, setPlatform] = useState("x");
    const [handle, setHandle] = useState("");
    const [name, setName] = useState("");

    const submit = () => {
        if (!handle.trim()) return;
        onAdd({ platform, handle: handle.trim(), name: name.trim() });
        setHandle(""); setName("");
    };

    return (
        <Card className="bg-paper border-electric/30 p-4" data-testid="social-add-card">
            <div className="flex items-center gap-2 mb-3">
                <Plus className="h-4 w-4 text-electric" />
                <h3 className="font-heading font-bold text-white uppercase tracking-wider text-sm">
                    Add Trusted Source
                </h3>
            </div>
            <div className="grid grid-cols-12 gap-2">
                <div className="col-span-3">
                    <Select value={platform} onValueChange={setPlatform}>
                        <SelectTrigger className="bg-obsidian border-border text-white" data-testid="social-platform-select">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="x">X (Twitter)</SelectItem>
                            <SelectItem value="instagram">Instagram</SelectItem>
                            <SelectItem value="facebook">Facebook</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <div className="col-span-4">
                    <Input
                        placeholder="handle (e.g. simbaSCTanzania)"
                        value={handle}
                        onChange={(e) => setHandle(e.target.value)}
                        className="bg-obsidian border-border text-white"
                        data-testid="social-handle-input"
                    />
                </div>
                <div className="col-span-3">
                    <Input
                        placeholder="Display name (optional)"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="bg-obsidian border-border text-white"
                        data-testid="social-name-input"
                    />
                </div>
                <div className="col-span-2">
                    <Button
                        onClick={submit}
                        disabled={busy || !handle.trim()}
                        className="w-full bg-electric hover:bg-electric/80 text-white font-heading font-bold uppercase tracking-wider"
                        data-testid="social-add-btn"
                    >
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="h-4 w-4 mr-1" />Add</>}
                    </Button>
                </div>
            </div>
            <p className="text-white/40 text-xs mt-2">
                We pull public posts from this handle every hour via free unofficial mirrors (Nitter/RSS-Bridge). No API keys needed.
            </p>
        </Card>
    );
};


const SourceRow = ({ src, onToggle, onRefresh, onDelete, busy, onOpenPreview }) => {
    const meta = PLATFORM_META[src.platform] || PLATFORM_META.x;
    const Icon = meta.Icon;
    return (
        <Card
            className="bg-paper border-border p-3 flex items-center gap-3 hover:border-electric/40 transition-colors"
            data-testid={`social-source-${src.id}`}
        >
            <div className={`${meta.color} border rounded-md p-2 flex-shrink-0`}>
                <Icon className="h-4 w-4" />
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-heading font-bold text-white text-sm truncate">
                        {src.name || `@${src.handle}`}
                    </span>
                    <Badge variant="outline" className="text-[10px] border-white/15 text-white/60 font-mono">
                        @{src.handle}
                    </Badge>
                    <Badge className={`${meta.color} border text-[10px] uppercase tracking-wider font-bold`}>
                        {meta.label}
                    </Badge>
                    {!src.active && (
                        <Badge className="bg-white/[0.05] text-white/40 border-white/10 border text-[10px] uppercase tracking-wider">
                            Paused
                        </Badge>
                    )}
                    {src.last_ok === false && (
                        <Badge className="bg-red-400/10 text-red-300 border-red-400/40 border text-[10px] uppercase tracking-wider">
                            <XCircle className="h-3 w-3 mr-1" /> Last fetch failed
                        </Badge>
                    )}
                    {src.last_ok === true && (
                        <Badge className="bg-emerald-400/10 text-emerald-300 border-emerald-400/40 border text-[10px] uppercase tracking-wider">
                            <CheckCircle2 className="h-3 w-3 mr-1" /> Live
                        </Badge>
                    )}
                </div>
                <div className="flex items-center gap-3 text-[11px] text-white/50 mt-1">
                    <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" /> {timeAgo(src.last_fetched_at)}
                    </span>
                    <span>{src.item_count ?? 0} posts</span>
                    {src.added_by && (
                        <span className="text-white/30">added by {src.added_by === "system-seed" ? "system" : src.added_by}</span>
                    )}
                </div>
            </div>
            <div className="flex items-center gap-1">
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onOpenPreview(src)}
                    className="text-white/60 hover:text-white text-xs"
                    data-testid={`social-preview-${src.id}`}
                >
                    Posts
                </Button>
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onRefresh(src)}
                    disabled={busy === src.id + ":refresh"}
                    className="text-electric hover:text-electric/80"
                    data-testid={`social-refresh-${src.id}`}
                    title="Refresh now"
                >
                    {busy === src.id + ":refresh" ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                </Button>
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onToggle(src)}
                    className={src.active ? "text-emerald-400" : "text-white/40"}
                    data-testid={`social-toggle-${src.id}`}
                    title={src.active ? "Pause" : "Resume"}
                >
                    {src.active ? <ToggleRight className="h-4 w-4" /> : <ToggleLeft className="h-4 w-4" />}
                </Button>
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onDelete(src)}
                    className="text-red-400 hover:text-red-300"
                    data-testid={`social-delete-${src.id}`}
                >
                    <Trash2 className="h-4 w-4" />
                </Button>
            </div>
        </Card>
    );
};


const PreviewDrawer = ({ src, onClose, authH }) => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!src) return;
        setLoading(true);
        axios
            .get(`${API}/admin/social-sources/${src.id}/recent?limit=20`, { headers: authH })
            .then((r) => setItems(r.data?.items || []))
            .catch(() => setItems([]))
            .finally(() => setLoading(false));
    }, [src, authH]);

    if (!src) return null;
    return (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end lg:items-center justify-center p-0 lg:p-8" onClick={onClose} data-testid="social-preview-drawer">
            <div className="bg-paper border border-border w-full lg:max-w-2xl rounded-t-2xl lg:rounded-2xl max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
                <div className="p-4 border-b border-border flex items-center justify-between">
                    <div>
                        <div className="text-xs text-white/50 uppercase tracking-wider">Recent posts</div>
                        <div className="font-heading font-bold text-white">{src.name} · @{src.handle}</div>
                    </div>
                    <Button variant="ghost" size="sm" onClick={onClose} className="text-white/60">
                        Close
                    </Button>
                </div>
                <div className="p-4 overflow-y-auto space-y-2">
                    {loading && <div className="text-center py-10 text-white/40"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></div>}
                    {!loading && items.length === 0 && (
                        <div className="text-center py-10 text-white/40 text-sm">
                            No posts yet. Click <RefreshCw className="h-3 w-3 inline mx-1" /> to fetch now.
                        </div>
                    )}
                    {items.map((it) => (
                        <Card key={it.id} className="bg-obsidian border-border p-3">
                            <div className="text-white text-sm font-medium leading-snug">{it.title}</div>
                            {it.summary && (
                                <div className="text-white/60 text-xs mt-1 line-clamp-3">{it.summary}</div>
                            )}
                            <div className="flex items-center gap-2 mt-2 text-[10px] text-white/40">
                                <span>{timeAgo(it.published_at)}</span>
                                {it.source_url && (
                                    <a
                                        href={it.source_url}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="text-electric hover:underline inline-flex items-center gap-1"
                                    >
                                        Open <ExternalLink className="h-3 w-3" />
                                    </a>
                                )}
                            </div>
                        </Card>
                    ))}
                </div>
            </div>
        </div>
    );
};


const SocialSources = () => {
    const { token } = useAuth();
    const authH = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

    const [sources, setSources] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(null);
    const [preview, setPreview] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await axios.get(`${API}/admin/social-sources`, { headers: authH });
            setSources(r.data?.sources || []);
        } catch (e) {
            toast.error("Failed to load sources");
        } finally {
            setLoading(false);
        }
    }, [authH]);

    useEffect(() => { load(); }, [load]);

    const add = async ({ platform, handle, name }) => {
        setBusy("add");
        try {
            await axios.post(`${API}/admin/social-sources`, { platform, handle, name }, { headers: authH });
            toast.success(`Added ${platform}/@${handle}`);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to add source");
        } finally { setBusy(null); }
    };

    const toggle = async (src) => {
        setBusy(src.id + ":toggle");
        try {
            await axios.patch(`${API}/admin/social-sources/${src.id}`, { active: !src.active }, { headers: authH });
            await load();
        } catch (e) {
            toast.error("Toggle failed");
        } finally { setBusy(null); }
    };

    const refreshOne = async (src) => {
        setBusy(src.id + ":refresh");
        try {
            const r = await axios.post(`${API}/admin/social-sources/${src.id}/refresh`, {}, { headers: authH });
            const d = r.data || {};
            toast.success(d.ok ? `Fetched ${d.fetched} · ${d.new} new` : "No posts returned", {
                description: d.ok ? undefined : "Mirror may be down; will retry hourly.",
            });
            await load();
        } catch (e) {
            toast.error("Refresh failed");
        } finally { setBusy(null); }
    };

    const refreshAll = async () => {
        setBusy("all");
        try {
            const r = await axios.post(`${API}/admin/social-sources/refresh-all`, {}, { headers: authH });
            toast.success(`Refreshed ${r.data?.sources || 0} sources · ${r.data?.new_items || 0} new posts`);
            await load();
        } catch (e) {
            toast.error("Refresh-all failed");
        } finally { setBusy(null); }
    };

    const del = async (src) => {
        if (!window.confirm(`Delete ${src.platform}/@${src.handle}?`)) return;
        setBusy(src.id + ":del");
        try {
            await axios.delete(`${API}/admin/social-sources/${src.id}`, { headers: authH });
            toast.success("Deleted");
            await load();
        } catch (e) {
            toast.error("Delete failed");
        } finally { setBusy(null); }
    };

    const grouped = useMemo(() => {
        const g = { x: [], instagram: [], facebook: [] };
        sources.forEach((s) => {
            (g[s.platform] || (g[s.platform] = [])).push(s);
        });
        return g;
    }, [sources]);

    return (
        <AdminLayout>
            <div className="max-w-5xl mx-auto p-6 space-y-5" data-testid="social-sources-page">
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                        <h1 className="font-heading font-black text-white text-2xl md:text-3xl uppercase tracking-tight">
                            <Hash className="h-6 w-6 inline mr-2 text-electric" />
                            Social Sources
                        </h1>
                        <p className="text-white/50 text-sm mt-1">
                            Trusted X · Instagram · Facebook handles Dimbani pulls posts from every hour.
                            Posts are searchable in Content Hub and answer SMS queries automatically.
                        </p>
                    </div>
                    <Button
                        onClick={refreshAll}
                        disabled={busy === "all"}
                        className="bg-electric hover:bg-electric/80 text-white font-heading font-bold uppercase tracking-wider"
                        data-testid="social-refresh-all-btn"
                    >
                        {busy === "all"
                            ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Refreshing...</>
                            : <><RefreshCw className="h-4 w-4 mr-2" /> Refresh All</>}
                    </Button>
                </div>

                <AddSourceCard onAdd={add} busy={busy === "add"} />

                {loading ? (
                    <div className="text-center py-16 text-white/40" data-testid="social-loading">
                        <Loader2 className="h-6 w-6 animate-spin mx-auto" />
                    </div>
                ) : (
                    ["x", "instagram", "facebook"].map((platform) => {
                        const list = grouped[platform] || [];
                        if (list.length === 0) return null;
                        const meta = PLATFORM_META[platform];
                        const Icon = meta.Icon;
                        return (
                            <div key={platform} data-testid={`social-group-${platform}`}>
                                <div className="flex items-center gap-2 mb-2">
                                    <Icon className="h-4 w-4 text-white/60" />
                                    <h2 className="text-xs uppercase tracking-widest text-white/60 font-bold">
                                        {meta.label}
                                    </h2>
                                    <span className="text-[10px] text-white/40">{list.length} sources</span>
                                </div>
                                <div className="space-y-2">
                                    {list.map((src) => (
                                        <SourceRow
                                            key={src.id}
                                            src={src}
                                            onToggle={toggle}
                                            onRefresh={refreshOne}
                                            onDelete={del}
                                            busy={busy}
                                            onOpenPreview={setPreview}
                                        />
                                    ))}
                                </div>
                            </div>
                        );
                    })
                )}
            </div>
            <PreviewDrawer src={preview} onClose={() => setPreview(null)} authH={authH} />
        </AdminLayout>
    );
};

export default SocialSources;
